import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * The register of dev-only harnesses: one entry per page, and the ONE place
 * they are declared.
 *
 * Both the router and the index at /dev read this list, which is the point.
 * A harness whose route was registered in App.tsx and whose existence was
 * advertised somewhere else would drift the moment anyone added a page and
 * only remembered one of the two -- and an index of dev tools that is missing
 * the tool you need is worse than no index, because it reads as "that does not
 * exist" rather than "look somewhere else".
 *
 * Adding a harness: write the page, add a row here. Nothing else.
 */
export interface DevPage {
  /** Route path, and the link the index renders. */
  path: string;
  /** What it is called. */
  title: string;
  /**
   * What it is FOR -- the question it answers, not the components it contains.
   * The index is read by someone who knows they have a problem and does not yet
   * know which harness looks at it.
   */
  blurb: string;
  /** Which heading it sits under on the index. */
  group: string;
  component: LazyExoticComponent<ComponentType>;
}

/**
 * The groups, in the order the index shows them. Declared rather than derived
 * so a new group lands where it was meant to instead of wherever its first
 * page happened to be added.
 */
export const DEV_GROUPS = ["Performance stage", "Components"] as const;

export const DEV_PAGES: DevPage[] = [
  {
    path: "/dev/story-cards",
    title: "Story cards",
    blurb:
      "Story cast poses over the backdrop, with responsive cast overflow. Uses the saved Atlantis cast.",
    group: "Components",
    component: lazy(() => import("@/pages/dev/StoryCardsPreviewPage")),
  },
  {
    path: "/dev/stage-script",
    title: "Stage script",
    blurb:
      "The full running order, played back. Every scene, every step, with the script pane, the lighting console, the cast browser and the layout controls. This is the real performance — the labs below are the same page with narrower content.",
    group: "Performance stage",
    component: lazy(() => import("@/pages/dev/stage/StageScriptTestPage")),
  },
  {
    path: "/dev/lights-lab",
    title: "Lighting lab",
    blurb:
      "Scenes built so that one wrong render is a single disagreement between a step's note and the stage. For when something looks darker than it should and you cannot tell whether that is the author, a preset, an uncleared cue or a regression.",
    group: "Performance stage",
    component: lazy(() => import("@/pages/dev/stage/LightLabPage")),
  },
  {
    path: "/dev/transitions-lab",
    title: "Transition lab",
    blurb:
      "Eight nearly-empty scenes, seven joins. All the behaviour is at the seams: continuations inheriting, blackouts, curtains, and the stage shifts in both directions. Play it forward, then backward — a join has to reverse itself.",
    group: "Performance stage",
    component: lazy(() => import("@/pages/dev/stage/TransitionLabPage")),
  },
  {
    path: "/dev/dance-lab",
    title: "Dance lab",
    blurb:
      "Grooves and pose sequences with nothing else going on: five dancers in time, a bar-synced loop, a clap on beat 3 of every other bar, a one-off jump. Pause, seek and change the speed; nobody may fall out of step.",
    group: "Performance stage",
    component: lazy(() => import("@/pages/dev/stage/DanceLabPage")),
  },
  {
    path: "/dev/stage-curtain",
    title: "Stage curtain",
    blurb:
      "The proscenium on its own: frame thickness, shadow, curtain travel, and how the panels open against images of different shapes. Its test images are generated with visible edges, so you can see whether the curtains opened to the artwork or past it.",
    group: "Components",
    component: lazy(() => import("@/pages/dev/StageCurtainTestPage")),
  },
  {
    path: "/dev/story-text",
    title: "Story text",
    blurb:
      "Speech bubbles and the narrator box against line lengths from four words to four clauses, at each position along the rim — where tails point, where long text wraps, and what collides.",
    group: "Components",
    component: lazy(() => import("@/pages/dev/StoryTextTestPage")),
  },
];
